import { useState, useEffect, useCallback } from 'react';

/**
 * 「添加到桌面 / 安装成 App」助手（全端通用）
 * - 手机（安卓 / 苹果 / 微信）：引导用浏览器"添加到主屏幕"，无需下载任何安装包
 * - 电脑（Chrome / Edge）：优先用浏览器原生「安装」事件一键装成独立窗口
 * - 任何页面都可以调用 openAddToDesktop() 打开图文向导（顶部 📲 按钮、下载页、登录页）
 */

interface BIPEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// 浏览器给的"可安装"事件只会在页面加载早期触发一次，必须用模块级变量接住
let deferredPrompt: BIPEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BIPEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

const EVT = 'erp:add-to-desktop';
/** 供任意位置调用：打开"添加到桌面"图文向导 */
export function openAddToDesktop() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVT));
}

const isStandalone = () => {
  try {
    return (
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (window.navigator as any).standalone === true
    );
  } catch {
    return false;
  }
};
const ua = () => (typeof navigator !== 'undefined' ? navigator.userAgent : '');
const isIOS = () => /iphone|ipad|ipod/i.test(ua());
const isAndroid = () => /android/i.test(ua());
const isWeChat = () => /micromessenger/i.test(ua());
const isMobileUA = () => isIOS() || isAndroid() || /mobile/i.test(ua());

/** 按当前设备生成对应操作步骤 */
function guideFor() {
  if (isWeChat()) {
    return {
      title: '微信里打开，请先这样',
      steps: [
        '点右上角「···」',
        '选择「在浏览器打开」',
        '到浏览器后，再按下面「安卓 / 苹果 / 电脑」对应步骤操作',
      ],
      note: '微信内置浏览器不允许直接安装到桌面，必须切到手机自带浏览器。',
    };
  }
  if (isIOS()) {
    return {
      title: 'iPhone / iPad（请用 Safari 打开）',
      steps: [
        '点屏幕底部中间的「分享」按钮（方框带向上箭头）',
        '在弹出列表里往下滑，找到「添加到主屏幕」并点击',
        '点右上角「添加」→ 桌面出现「财务ERP」图标',
      ],
      note: '必须用 Safari；若是微信/其它 App 里打开的，先「在浏览器打开」。',
    };
  }
  if (isAndroid()) {
    return {
      title: '安卓手机 / 平板',
      steps: [
        '点浏览器右上角「⋮」（或「≡」）菜单',
        '点「安装应用」或「添加到主屏幕」',
        '确认后 → 桌面出现「财务ERP」图标，点开即全屏，像 App 一样',
      ],
      note: '推荐用 Chrome 或手机自带浏览器；没有该选项就换成 Chrome 再试一次。',
    };
  }
  return {
    title: '电脑端（Chrome / Edge 浏览器）',
    steps: [
      '点地址栏最右侧的「安装」小图标（⊕ 或显示器带箭头）',
      '在弹出的窗口点「安装」',
      '桌面 / 开始菜单出现「财务ERP」图标，双击即用',
    ],
    note: '找不到安装图标？点浏览器右上角「⋮」→「安装 财务ERP」。',
  };
}

export default function InstallPrompt() {
  const [showBar, setShowBar] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [hasNative, setHasNative] = useState(!!deferredPrompt);

  useEffect(() => {
    const onNative = () => setHasNative(!!deferredPrompt);
    listeners.add(onNative);
    const onOpenGuide = () => {
      setShowGuide(true);
      setShowBar(false);
    };
    window.addEventListener(EVT, onOpenGuide);

    let t: ReturnType<typeof setTimeout> | undefined;
    if (!isStandalone()) {
      if (deferredPrompt) {
        // 电脑/安卓 Chrome：拿到原生安装事件，延迟出现不打扰
        t = setTimeout(() => setShowBar(true), 2500);
      } else if (isMobileUA() && !sessionStorage.getItem('erp_install_hint')) {
        // 苹果/微信等没有原生事件的：也给一次引导（每次会话一次）
        t = setTimeout(() => {
          setShowBar(true);
          sessionStorage.setItem('erp_install_hint', '1');
        }, 3500);
      }
    }
    return () => {
      listeners.delete(onNative);
      window.removeEventListener(EVT, onOpenGuide);
      if (t) clearTimeout(t);
    };
  }, []);

  const install = useCallback(async () => {
    const p = deferredPrompt;
    if (!p) {
      setShowBar(false);
      setShowGuide(true);
      return;
    }
    try {
      await p.prompt();
      const { outcome } = await p.userChoice;
      deferredPrompt = null;
      setHasNative(false);
      if (outcome === 'accepted') setShowBar(false);
      else setShowBar(false);
    } catch {
      setShowGuide(true);
      setShowBar(false);
    }
  }, []);

  if (isStandalone()) return null;

  const g = guideFor();

  return (
    <>
      {/* ===== 底部悬浮提示条 ===== */}
      {showBar && (
        <div className="fixed bottom-20 md:bottom-4 left-3 right-3 md:left-auto md:right-4 md:w-96 z-[65] bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl shadow-2xl p-4 animate-slide-up">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-bold">ER</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">把「财务ERP」放到桌面，像 App 一样</p>
              <p className="text-xs text-blue-100 mt-0.5">不下载任何安装包，只在本机加个图标</p>
            </div>
            <button
              onClick={() => setShowBar(false)}
              className="text-blue-200 hover:text-white text-lg leading-none"
              aria-label="关闭"
            >
              ✕
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={install}
              className="flex-1 bg-white text-blue-600 font-semibold text-sm py-2 rounded-xl hover:bg-blue-50 transition"
            >
              📲 立即安装
            </button>
            <button
              onClick={() => {
                setShowBar(false);
                setShowGuide(true);
              }}
              className="px-3 text-sm py-2 rounded-xl border border-white/30 hover:bg-white/10 transition whitespace-nowrap"
            >
              查看步骤
            </button>
          </div>
        </div>
      )}

      {/* ===== 图文安装向导 ===== */}
      {showGuide && (
        <div
          className="fixed inset-0 z-[80] bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4"
          onClick={() => setShowGuide(false)}
        >
          <div
            className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl shadow-2xl p-5 max-h-[86vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-800">📲 添加到桌面（像 App 一样点开就用）</h3>
              <button
                onClick={() => setShowGuide(false)}
                className="text-slate-400 hover:text-slate-700 text-xl leading-none"
                aria-label="关闭"
              >
                ✕
              </button>
            </div>

            <div className="bg-slate-50 rounded-xl p-3 mb-3">
              <p className="font-semibold text-slate-800 text-sm mb-2">{g.title}</p>
              <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
                {g.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
              {g.note && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2 mt-2">💡 {g.note}</p>}
            </div>

            {hasNative && (
              <button
                onClick={install}
                className="w-full bg-brand-600 text-white font-bold rounded-xl py-3 mb-2 hover:bg-brand-700 active:scale-95 transition"
              >
                📲 一键安装（推荐）
              </button>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => {
                  const url = location.origin + '/login';
                  navigator.clipboard?.writeText(url).then(
                    () => alert('网址已复制：' + url + '\n\n可在电脑浏览器粘贴打开，再点右上角「安装」图标。'),
                    () => alert('请手动复制网址：' + url)
                  );
                }}
                className="flex-1 text-sm py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
              >
                📋 复制网址
              </button>
              <button
                onClick={() => setShowGuide(false)}
                className="flex-1 text-sm py-2.5 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition"
              >
                我知道了
              </button>
            </div>

            <p className="text-[11px] text-slate-400 mt-3 text-center">
              提示：添加到桌面后，启动更快、没有浏览器地址栏，和装好的软件体验一致。
            </p>
          </div>
        </div>
      )}
    </>
  );
}
